import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { CtaButton } from "@/components/CtaButton";
import { SectionHead, Reveal, Em } from "@/components/Reveal";
import { LeopardFrame } from "@/components/Leopard";
import { site } from "@/config/site.config";

export const VideoSection = () => {
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState(false);
  const hasVideo = Boolean(site.videoUrl);

  const toEmbed = (url) => {
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/))([\w-]{6,})/);
    if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0`;
    return url;
  };

  const handlePlay = () => {
    if (hasVideo) setOpen(true);
    else {
      setNotice(true);
      setTimeout(() => setNotice(false), 2200);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <section data-testid="video-section" className="mx-auto max-w-6xl px-5 py-20 sm:py-28">
      <SectionHead eyebrow="A MINUTE WITH DASHA" center>
        BEFORE YOU MAKE
        <br />
        ANOTHER PLAN… GET HONEST
        <br />
        ABOUT WHAT YOU <Em>ACTUALLY WANT.</Em>
      </SectionHead>

      <Reveal className="mx-auto max-w-4xl">
        <LeopardFrame>
          <button
            data-testid="video-placeholder"
            onClick={handlePlay}
            aria-label="Play a minute with Dasha"
            className="group relative block aspect-video w-full overflow-hidden border border-white/10"
          >
            <img
              src="/assets/dasha-poster.jpg"
              alt="Dasha Love"
              className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
            />
            <div className="absolute inset-0 bg-ink/45 transition-colors duration-500 group-hover:bg-ink/30" />
            <span className="absolute left-4 top-4 border border-white/20 bg-ink/60 px-4 py-1.5 font-mono text-[10px] tracking-[0.3em] text-cream backdrop-blur-sm">
              {site.videoLabel}
            </span>
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-lime text-ink shadow-[0_0_60px_rgba(201,163,92,0.35)] transition-transform duration-500 group-hover:scale-110">
                <Play className="ml-1 h-8 w-8 fill-current" />
              </span>
              <span className="font-mono text-xs tracking-[0.35em] text-cream">PRESS PLAY</span>
              <span className="font-serif text-lg italic text-cream/80">A MINUTE WITH DASHA</span>
            </span>
            <AnimatePresence>
              {notice && (
                <motion.span
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-ink/80 px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-lime backdrop-blur-sm"
                >
                  SALES VIDEO DROPS HERE — URL PENDING
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </LeopardFrame>
        <p className="mt-6 text-center font-serif text-xl italic text-cream/80 sm:text-2xl">
          “YOUR VISION MAY NOT NEED MORE TIME. IT MAY NEED A DECISION.”
        </p>
      </Reveal>

      <Reveal className="mx-auto mt-12 max-w-2xl space-y-4 text-center text-base text-cream/70 sm:text-lg">
        <p>What if the reason you aren’t creating what you want isn’t a lack of talent, discipline or potential?</p>
        <p className="text-cream">
          What if you simply haven’t chosen a vision powerful enough to organize your energy around?
        </p>
        <div className="pt-4">
          <CtaButton id="video">CHOOSE MY VISION</CtaButton>
        </div>
      </Reveal>

      <AnimatePresence>
        {open && hasVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/90 p-4 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          >
            <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <button data-testid="video-close" onClick={() => setOpen(false)} aria-label="Close video" className="absolute -top-12 right-0 text-cream hover:text-lime">
                <X className="h-7 w-7" />
              </button>
              {/\.mp4($|\?)/.test(site.videoUrl) ? (
                <video src={site.videoUrl} controls autoPlay className="aspect-video w-full" />
              ) : (
                <iframe src={toEmbed(site.videoUrl)} title="A minute with Dasha" allow="autoplay; fullscreen" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen className="aspect-video w-full border-0" />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
